import {
  QueryObserverResult,
  UseMutationResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { APIClient } from 'api/APIClient';
import {
  ProjectSearchResults,
  SearchProjectOrEmployeeQuery,
  TravelFormQuery,
  TravelRequestResult,
} from 'api/types/types';
import { QueryKeys } from 'constants/queryKeys';

async function submitTravelForm(query: TravelFormQuery): Promise<unknown> {
  const { data } = await APIClient().post('/request', query);
  return data;
}

export function useTravelFormMutation(): UseMutationResult<
  unknown,
  unknown,
  TravelFormQuery,
  unknown
> {
  return useMutation<unknown, unknown, TravelFormQuery, unknown>({
    mutationFn: (query: TravelFormQuery) => submitTravelForm(query),
    mutationKey: [QueryKeys.TRAVEL_FORM_QUERY],
    meta: {
      successMessage: 'Ferðabeiðni hefur verið skráð',
      errorMessage: 'Ekki tókst að vista formið. Reyndu aftur síðar',
    },
  });
}

async function retrieveTravelRequest<T>(
  id: string | null,
  role: string | null
): Promise<T> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const roleString = role ? `?role=${role}` : '';
  const { data } = await APIClient().get(`/request/${id}${roleString}`);
  if (data?.message) throw new Error(data.message);
  return data;
}

export function useRetrieveTravelRequestById(
  id: string | null,
  role: string | null
): QueryObserverResult<TravelRequestResult, unknown> {
  return useQuery<TravelRequestResult, unknown>({
    queryKey: [QueryKeys.TRAVEL_QUERY_BY_ID, id, role],
    queryFn: () => retrieveTravelRequest<TravelRequestResult>(id, role),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: false,
    meta: {
      errorMessage: 'Ferðabeiðni fannst ekki',
    },
  });
}

async function updateTravelForm<T>(
  id: string | null,
  query: TravelFormQuery
): Promise<T> {
  if (typeof id === 'undefined') {
    throw new Error('TravelId is undefined');
  }
  const { data } = await APIClient().put(`/request/${id}`, query);
  return data;
}
export function useUpdateTravelRequest(
  id: string | null
): UseMutationResult<unknown, unknown, TravelFormQuery, unknown> {
  return useMutation<unknown, unknown, TravelFormQuery, unknown>({
    mutationFn: (query: TravelFormQuery) => updateTravelForm(id, query),
    mutationKey: [QueryKeys.TRAVEL_FORM_QUERY],
    // meta: {
    //   successMessage: `Ferðabeiðni (${id}) hefur verið uppfærð`,
    //   errorMessage: 'Ekki tókst að uppfæra ferðabeiðnina. Reyndu aftur síðar',
    // },
  });
}

async function searchEmployee(
  query: SearchProjectOrEmployeeQuery
): Promise<ProjectSearchResults> {
  const { data } = await APIClient().post('/employee/search', query);
  return data;
}

export function useSearchEmployeeQuery(): UseMutationResult<
  ProjectSearchResults,
  unknown,
  SearchProjectOrEmployeeQuery,
  unknown
> {
  return useMutation<
    ProjectSearchResults,
    unknown,
    SearchProjectOrEmployeeQuery,
    unknown
  >({
    mutationFn: (query: SearchProjectOrEmployeeQuery) => searchEmployee(query),
    mutationKey: [QueryKeys.EMPLOYEE_SEARCH],
  });
}
